/**
 * Detect whether an event/show requires reserved seating (seats.io flow).
 * Only API/show flags — never treat local seats.io env defaults as "enabled".
 */

type RecordLike = Record<string, unknown>;

function readString(record: RecordLike | null | undefined, keys: string[]): string {
  if (!record) return '';
  for (const key of keys) {
    const value = record[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return '';
}

function readTruthyFlag(record: RecordLike | null | undefined, keys: string[]): boolean {
  if (!record) return false;
  for (const key of keys) {
    const value = record[key];
    if (value === true || value === 1 || value === '1') return true;
    if (typeof value === 'string') {
      const n = value.trim().toLowerCase();
      if (n === 'true' || n === 'yes' || n === 'enabled' || n === 'seated' || n === 'on') return true;
    }
  }
  return false;
}

function readFalsyFlag(record: RecordLike | null | undefined, keys: string[]): boolean {
  if (!record) return false;
  for (const key of keys) {
    const value = record[key];
    if (value === false || value === 0 || value === '0') return true;
    if (typeof value === 'string') {
      const n = value.trim().toLowerCase();
      if (n === 'false' || n === 'no' || n === 'disabled' || n === 'off' || n === 'general') return true;
    }
  }
  return false;
}

function isSeatingTypeSeated(record: RecordLike | null | undefined): boolean {
  const value = readString(record, ['seating_type', 'seatingType', 'seat_type', 'seatType']).toLowerCase();
  return value === 'seated' || value === 'seat' || value === 'seats' || value === 'seating' || value === 'reserved';
}

function isSeatingTypeGeneral(record: RecordLike | null | undefined): boolean {
  const value = readString(record, ['seating_type', 'seatingType', 'seat_type', 'seatType']).toLowerCase();
  return (
    value === 'general' ||
    value === 'ga' ||
    value === 'general_admission' ||
    value === 'general-admission' ||
    value === 'non_seated' ||
    value === 'non-seated' ||
    value === 'standing'
  );
}

/** Merge event + show; show wins for overlapping keys. */
export function mergeSeatingSources(
  event: RecordLike | null | undefined,
  show: RecordLike | null | undefined
): RecordLike {
  return { ...(event ?? {}), ...(show ?? {}) };
}

/**
 * True when this show/event should open the seats.io selection step
 * after ADD TO CART. Disabled / general admission → normal review flow.
 */
export function isShowSeatingEnabled(
  event: RecordLike | null | undefined,
  show: RecordLike | null | undefined
): boolean {
  const merged = mergeSeatingSources(event, show);

  // Explicit off wins.
  if (
    readFalsyFlag(merged, [
      'is_seated',
      'seated',
      'is_seating',
      'seating_enabled',
      'enable_seating',
      'has_seating',
      'has_seating_chart'
    ])
  ) {
    return false;
  }
  if (isSeatingTypeGeneral(merged)) return false;

  if (
    readTruthyFlag(merged, [
      'is_seated',
      'seated',
      'is_seating',
      'seating_enabled',
      'enable_seating',
      'has_seating',
      'has_seating_chart'
    ])
  ) {
    return true;
  }
  if (isSeatingTypeSeated(merged)) return true;

  const bookingType = readString(merged, ['booking_type', 'ticket_booking_type']).toLowerCase();
  if (['seated', 'seat', 'seats', 'seating', 'reserved'].includes(bookingType)) return true;

  // Chart/event keys from API (not env defaults) also mean seated flow.
  const hasApiChartOrEvent = !!(
    readString(merged, [
      'seatsio_event_key',
      'seats_io_event_key',
      'seatsio_chart_key',
      'seats_io_chart_key',
      'seating_chart_key',
      'chart_key',
      'seat_map_key'
    ]) ||
    readString(
      (merged['seating_config'] as RecordLike) ||
        (merged['seats_io'] as RecordLike) ||
        (merged['seatsio'] as RecordLike) ||
        null,
      ['event_key', 'seatsio_event_key', 'chart_key', 'seatsio_chart_key']
    )
  );
  return hasApiChartOrEvent;
}
